// =============================================================================
// src/data/analytics-feed-page-client.ts — Busca de UMA pagina do feed.
//
// 2026-09-20. Modulo proprio, e nao mais um metodo em analytics-client (338
// linhas), por SRP e porque o ciclo de vida e outro: o dashboard e buscado uma
// vez por RECORTE e memoizado; a pagina e buscada a cada clique em "proxima",
// em "ordenar" ou num chip de canal, e NAO pode re-hidratar o dashboard junto —
// isso reexecutaria os 14 roots, incluindo o GROUP BY de ~110 ms do frescor,
// a cada virada de pagina, alem de remontar a rota e perder scroll e foco.
//
// A CONTAGEM e deliberadamente separada e tolerante a falha: ela alimenta
// "51–75 de 21.195" e o numero de paginas, mas a pagina tem de aparecer mesmo
// quando o _aggregate nao responde (a nav degrada para anterior/proxima).
// =============================================================================

import {
  ANALYTICS_FEED_PAGE_SIZE,
  buildAnalyticsFeedPageQuery,
  buildAnalyticsFeedTotalQuery,
  type AnalyticsFeedPageQueryOptions,
} from './analytics-feed-page-query';
import { postHasuraGraphql } from './hasura-graphql-post';
import type { AnalyticsMentionsFeedRow } from './analytics-live-query';

/** Recorte + pagina + a fronteira da janela do periodo, ja resolvida em ISO. */
export interface AnalyticsFeedPageRequest extends AnalyticsFeedPageQueryOptions {
  readonly sinceTs: string;
}

export interface AnalyticsFeedPageDeps {
  readonly endpoint: string;
  readonly clientId: string;
  readonly fetchImpl: typeof fetch;
  readonly timeoutMs: number;
}

/** Linhas da pagina + se existe proxima (derivado da 26a linha, nao da contagem). */
export interface AnalyticsFeedPageRows {
  readonly rows: readonly AnalyticsMentionsFeedRow[];
  readonly hasNextPage: boolean;
}

interface AnalyticsFeedPageGraphqlData {
  readonly feedPage?: readonly AnalyticsMentionsFeedRow[];
}

interface AnalyticsFeedTotalGraphqlData {
  readonly feedTotal?: { readonly aggregate?: { readonly count?: number } };
}

function analyticsFeedPageVariables(deps: AnalyticsFeedPageDeps, request: AnalyticsFeedPageRequest): Record<string, unknown> {
  return { clientId: deps.clientId, sinceTs: request.sinceTs };
}

/**
 * Uma pagina do feed. A consulta pede 26 linhas e esta funcao descarta a 26a:
 * ela existe so para responder `hasNextPage` sem depender da contagem.
 *
 * @example fetchAnalyticsFeedPage(deps, { ...recorte, page: 3, sinceTs }) // { rows: 25 itens, hasNextPage: true }
 */
export async function fetchAnalyticsFeedPage(
  deps: AnalyticsFeedPageDeps,
  request: AnalyticsFeedPageRequest,
): Promise<AnalyticsFeedPageRows> {
  const data = await postHasuraGraphql<AnalyticsFeedPageGraphqlData>({
    endpoint: deps.endpoint,
    clientId: deps.clientId,
    query: buildAnalyticsFeedPageQuery(request),
    variables: analyticsFeedPageVariables(deps, request),
    fetchImpl: deps.fetchImpl,
    timeoutMs: deps.timeoutMs,
    errorLabel: 'Analytics',
  });
  const probedRows = data.feedPage ?? [];
  return {
    rows: probedRows.slice(0, ANALYTICS_FEED_PAGE_SIZE),
    hasNextPage: probedRows.length > ANALYTICS_FEED_PAGE_SIZE,
  };
}

/**
 * Total do recorte, ou null quando a contagem nao responde.
 *
 * NUNCA lanca: o `_aggregate` depende de permissao propria no Hasura (a view de
 * chaves, migration 031) e de um documento separado. Falhar aqui degrada a
 * paginacao para anterior/proxima — nunca derruba a pagina que ja veio.
 *
 * @example await fetchAnalyticsFeedTotal(deps, request) // 21195 | null
 */
export async function fetchAnalyticsFeedTotal(
  deps: AnalyticsFeedPageDeps,
  request: AnalyticsFeedPageRequest,
): Promise<number | null> {
  try {
    const data = await postHasuraGraphql<AnalyticsFeedTotalGraphqlData>({
      endpoint: deps.endpoint,
      clientId: deps.clientId,
      query: buildAnalyticsFeedTotalQuery(request),
      variables: analyticsFeedPageVariables(deps, request),
      fetchImpl: deps.fetchImpl,
      timeoutMs: deps.timeoutMs,
      errorLabel: 'Analytics',
    });
    return data.feedTotal?.aggregate?.count ?? null;
  } catch {
    return null;
  }
}
